import { useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import cssText from "./QuayThuV4Light.shadow.css";
import panels from "./panels";
import lots from "./lots";

const HOST_STYLE = {
  all: "initial",
  display: "block",
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  padding: 0,
  border: 0,
  background: "transparent",
  boxSizing: "border-box",
  lineHeight: "normal",
  color: "#000",
  fontFamily: '"Arial, Tahoma, Helvetica, Lexend Deca", sans-serif',
  fontSize: "15px",
};

const REGION_ALIAS = {
  mb: "mb",
  bac: "mb",
  mienbac: "mb",
  xsmb: "mb",
  mn: "mn",
  nam: "mn",
  miennam: "mn",
  xsmn: "mn",
  mt: "mt",
  trung: "mt",
  mientrung: "mt",
  xsmt: "mt",
  vl: "vl",
  vietlott: "vl",
  vietlot: "vl",
};

export function resolveRegion(value) {
  const compact = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, "");
  return REGION_ALIAS[compact] || "mb";
}

const MB_KEYS = ["1", "2", "3", "4", "5", "6", "7", "MaDb", "DB"];
const SOUTH_KEYS = ["8", "7", "6", "5", "4", "3", "2", "1", "DB"];
const MB_PROVINCES = new Set(["mien-bac", "ha-noi", "quang-ninh", "bac-ninh", "hai-phong", "nam-dinh", "thai-binh"]);
const MT_PROVINCES = new Set([
  "binh-dinh",
  "da-nang",
  "dac-lac",
  "quang-ngai",
  "gia-lai",
  "khanh-hoa",
  "kon-tum",
  "ninh-thuan",
  "thua-thien-hue",
  "quang-tri",
  "dac-nong",
  "quang-nam",
  "quang-binh",
  "phu-yen",
]);

const CAN = ["Giáp", "Ất", "Bính", "Đinh", "Mậu", "Kỷ", "Canh", "Tân", "Nhâm", "Quý"];
const CHI = ["Tý", "Sửu", "Dần", "Mão", "Thìn", "Tỵ", "Ngọ", "Mùi", "Thân", "Dậu", "Tuất", "Hợi"];
const MENH = [
  ["Kim", "trắng, bạc"],
  ["Thủy", "đen, xanh nước biển"],
  ["Hỏa", "đỏ, cam"],
  ["Mộc", "xanh lá"],
  ["Thổ", "vàng, nâu"],
];

function regionOfProvince(value) {
  if (!value) return null;
  if (MB_PROVINCES.has(value)) return "mb";
  if (MT_PROVINCES.has(value)) return "mt";
  return "mn";
}

function pad(value, size = 2) {
  return String(value).padStart(size, "0");
}

function digitsOf(value) {
  return String(value ?? "").replace(/\D/g, "");
}

function todayLabel() {
  const date = new Date();
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
}

function flatten(lot, keys) {
  const list = [];
  keys.forEach((key) => {
    (lot[key] || []).forEach((value, index) => {
      list.push({ key, index, value: String(value) });
    });
  });
  return list;
}

function revealFlat(flat, count) {
  const lot = {};
  flat.forEach((cell) => {
    if (!lot[cell.key]) lot[cell.key] = [];
  });
  flat.forEach((cell, index) => {
    lot[cell.key][cell.index] = index < count ? cell.value : "";
  });
  return lot;
}

function provinceCounts(total, step) {
  const base = Math.floor(step / total);
  const extra = step % total;
  return Array.from({ length: total }, (_, index) => base + (index < extra ? 1 : 0));
}

function decorateMb(lot) {
  const db = digitsOf(Array.isArray(lot.DB) ? lot.DB[0] : lot.DB);
  let last = "";
  for (let prize = 1; prize <= 7; prize += 1) {
    (lot[prize] || []).forEach((value) => {
      const plain = digitsOf(value);
      if (plain) last = plain;
    });
  }
  if (db) last = db;
  const mark = db && db === last ? "" : last;
  const out = { DB: db && db === last ? `<span class="cl-red">${db}</span>` : db, MaDb: lot.MaDb || [] };
  for (let prize = 1; prize <= 7; prize += 1) {
    out[prize] = (lot[prize] || []).map((value) => {
      const plain = digitsOf(value);
      if (mark && plain && plain === mark) return `<span class="clnote">${plain}</span>`;
      return plain;
    });
  }
  return out;
}

function decorateSouth(lot) {
  const db = digitsOf(Array.isArray(lot.DB) ? lot.DB[0] : lot.DB);
  let last = "";
  for (let prize = 8; prize >= 1; prize -= 1) {
    (lot[prize] || []).forEach((value) => {
      const plain = digitsOf(value);
      if (plain) last = plain;
    });
  }
  if (db) last = db;
  const mark = db && db === last ? "" : last;
  const out = { DB: db && db === last ? `<span class="cl-red">${db}</span>` : db };
  for (let prize = 1; prize <= 8; prize += 1) {
    out[prize] = (lot[prize] || []).map((value) => {
      const plain = digitsOf(value);
      if (mark && plain && plain === mark) return `<span class="cl-red">${plain}</span>`;
      return plain;
    });
  }
  return out;
}

function showNumber(node, value) {
  if (!node) return;
  const html = Array.isArray(value) ? value.join("") : String(value ?? "");
  const plain = digitsOf(html.replace(/<[^>]+>/g, ""));
  if (!plain) {
    node.classList.add("imgloadig");
    node.innerHTML = "";
    node.removeAttribute("data-full");
    node.removeAttribute("data-html");
    return;
  }
  node.classList.remove("imgloadig");
  node.setAttribute("data-full", plain);
  node.setAttribute("data-html", html);
  node.innerHTML = html;
}

function applyDigits(root) {
  const checked = root.querySelector(".digits-form input:checked");
  const count = checked ? Number(checked.value) : 0;
  root.querySelectorAll("[data-nc]").forEach((node) => {
    const full = node.getAttribute("data-full");
    if (!full) return;
    if (!count) {
      node.innerHTML = node.getAttribute("data-html") || full;
      return;
    }
    node.textContent = full.slice(-count);
  });
}

function joined(list, highlight) {
  let html = "";
  let marked = false;
  list.forEach((item) => {
    if (!marked && highlight && item === highlight) {
      marked = true;
      html += `,<span class="clnote">${item}</span>`;
      return;
    }
    html += `,${item}`;
  });
  return html ? html.slice(1) : "";
}

function fillLotoMb(root, lot) {
  const db = digitsOf(Array.isArray(lot.DB) ? lot.DB[0] : lot.DB).slice(-2);
  const dau = Array.from({ length: 10 }, () => []);
  const duoi = Array.from({ length: 10 }, () => []);
  ["1", "2", "3", "4", "5", "6", "7", "DB"].forEach((key) => {
    (lot[key] || []).forEach((value) => {
      const number = digitsOf(value);
      if (number.length < 2) return;
      dau[Number(number.slice(-2, -1))].push(number.slice(-1));
      duoi[Number(number.slice(-1))].push(number.slice(-2, -1));
    });
  });
  const head = db.slice(0, 1);
  const tail = db.slice(1);
  for (let index = 0; index < 10; index += 1) {
    const headCell = root.querySelector(`.v-loto-dau-${index}`);
    const tailCell = root.querySelector(`.v-loto-duoi-${index}`);
    dau[index].sort();
    duoi[index].sort();
    if (headCell) headCell.innerHTML = joined(dau[index], head && String(index) === head ? tail : "");
    if (tailCell) tailCell.innerHTML = joined(duoi[index], tail && String(index) === tail ? head : "");
  }
}

function fillLotoSouth(root, lot, province) {
  const dau = Array.from({ length: 10 }, () => []);
  SOUTH_KEYS.forEach((key) => {
    if (key === "DB" || key === "MaDb") return;
    (lot[key] || []).forEach((value) => {
      const number = digitsOf(value);
      if (number.length < 2) return;
      dau[Number(number.slice(-2, -1))].push(number.slice(-1));
    });
  });
  const db = digitsOf(Array.isArray(lot.DB) ? lot.DB[0] : lot.DB);
  if (db.length >= 2) dau[Number(db.slice(-2, -1))].push(db.slice(-1));
  for (let index = 0; index < 10; index += 1) {
    const cell = root.querySelectorAll(`.v-loto-dau-${index}`)[province];
    if (cell) cell.innerHTML = dau[index].join(",");
  }
}

function paintMb(root, decorated, raw) {
  const board = root.querySelector("[data-id='kq']");
  if (!board) return;
  showNumber(board.querySelector(".v-gdb"), decorated.DB);
  showNumber(board.querySelector(".v-g1"), decorated[1]?.[0] || "");
  (decorated[2] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g2-${index}`), value));
  (decorated[3] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g3-${index}`), value));
  (decorated[4] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g4-${index}`), value));
  (decorated[5] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g5-${index}`), value));
  (decorated[6] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g6-${index}`), value));
  (decorated[7] || []).forEach((value, index) => showNumber(board.querySelector(`.v-g7-${index}`), value));
  const madb = board.querySelector(".v-madb");
  if (madb) {
    const codes = (decorated.MaDb || []).filter(Boolean);
    madb.innerHTML = codes.map((code) => `<b>${code}</b>`).join(" - ");
  }
  fillLotoMb(root, raw);
  applyDigits(root);
}

function paintSouth(root, decorated, raw, province) {
  const board = root.querySelector("[data-id='kq']");
  if (!board) return;
  const at = (cls) => board.querySelectorAll(cls)[province];
  showNumber(at(".v-g8"), decorated[8]?.[0] || "");
  showNumber(at(".v-g7"), decorated[7]?.[0] || "");
  (decorated[6] || []).forEach((value, index) => showNumber(at(`.v-g6-${index}`), value));
  showNumber(at(".v-g5"), decorated[5]?.[0] || "");
  (decorated[4] || []).forEach((value, index) => showNumber(at(`.v-g4-${index}`), value));
  (decorated[3] || []).forEach((value, index) => showNumber(at(`.v-g3-${index}`), value));
  showNumber(at(".v-g2"), decorated[2]?.[0] || "");
  showNumber(at(".v-g1"), decorated[1]?.[0] || "");
  showNumber(at(".v-gdb"), decorated.DB);
  fillLotoSouth(root, raw, province);
  applyDigits(root);
}

function zodiac(day, month) {
  const signs = [
    [1, 19, "Ma Kết"],
    [2, 18, "Bảo Bình"],
    [3, 20, "Song Ngư"],
    [4, 19, "Bạch Dương"],
    [5, 20, "Kim Ngưu"],
    [6, 21, "Song Tử"],
    [7, 22, "Cự Giải"],
    [8, 22, "Sư Tử"],
    [9, 22, "Xử Nữ"],
    [10, 23, "Thiên Bình"],
    [11, 22, "Bọ Cạp"],
    [12, 21, "Nhân Mã"],
    [12, 31, "Ma Kết"],
  ];
  const found = signs.find(([m, d]) => month < m || (month === m && day <= d));
  return found ? found[2] : "Ma Kết";
}

function hashText(value) {
  let hash = 0;
  const text = String(value);
  for (let index = 0; index < text.length; index += 1) hash = (hash * 33 + text.charCodeAt(index)) >>> 0;
  return hash;
}

function QuayThuV4Light({ region: regionProp }) {
  const initialRegion = resolveRegion(regionProp);
  const rootRef = useRef(null);
  const regionRef = useRef(initialRegion);
  const provinceRef = useRef("");
  const timersRef = useRef(new Map());
  const [region, setRegion] = useState(initialRegion);

  const clearTimer = (key) => {
    const current = timersRef.current.get(key);
    if (!current) return;
    clearInterval(current);
    clearTimeout(current);
    timersRef.current.delete(key);
  };

  const track = (key, id) => {
    clearTimer(key);
    timersRef.current.set(key, id);
    return id;
  };

  const clearAll = () => {
    timersRef.current.forEach((id) => {
      clearInterval(id);
      clearTimeout(id);
    });
    timersRef.current.clear();
  };

  const startTrial = (root, current) => {
    clearTimer("trial");
    if (current === "mb") {
      const flat = flatten(lots.mb.lotData, MB_KEYS);
      let step = 0;
      const tick = () => {
        step += 1;
        const partial = revealFlat(flat, step);
        paintMb(root, decorateMb(partial), partial);
        if (step >= flat.length) clearTimer("trial");
      };
      tick();
      track("trial", setInterval(tick, 500));
      return;
    }
    const source = lots[current];
    if (!Array.isArray(source)) return;
    const flats = source.map((item) => flatten(item.lotData, SOUTH_KEYS));
    const total = flats.reduce((sum, list) => sum + list.length, 0);
    let step = 0;
    const tick = () => {
      step += 1;
      const counts = provinceCounts(source.length, step);
      source.forEach((_, index) => {
        const partial = revealFlat(flats[index], counts[index]);
        paintSouth(root, decorateSouth(partial), partial, index);
      });
      if (step >= total) clearTimer("trial");
    };
    tick();
    track("trial", setInterval(tick, 500));
  };

  const spinBalls = (root, kind) => {
    const id = kind === "mega" ? "load_kq_mega_0" : "load_kq_power_0";
    const values = lots[kind];
    const nodes = [...root.querySelectorAll(`#${id} .data td i`)];
    nodes.forEach((node) => {
      node.classList.add("imgloadig");
      node.textContent = "";
    });
    let index = 0;
    const timer = setInterval(() => {
      const node = nodes[index];
      if (!node) {
        clearTimer(kind);
        return;
      }
      node.classList.remove("imgloadig");
      node.textContent = values[index] || "";
      index += 1;
      if (index >= values.length) clearTimer(kind);
    }, 2000);
    track(kind, timer);
  };

  useLayoutEffect(() => {
    regionRef.current = region;
    const root = rootRef.current;
    if (!root) return;
    clearAll();
    root.innerHTML = `<div class="qtv4"><div class="col-l">${panels[region]}</div></div>`;
    if (provinceRef.current) {
      const select = root.querySelector("#province_name");
      if (select && [...select.options].some((option) => option.value === provinceRef.current)) {
        select.value = provinceRef.current;
      }
    }
  }, [region]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onSubmit = (event) => event.preventDefault();
    const onChange = (event) => {
      if (event.target?.id === "province_name") {
        const next = regionOfProvince(event.target.value);
        if (!next || next === regionRef.current) return;
        provinceRef.current = event.target.value;
        setRegion(next);
        return;
      }
      if (event.target?.closest?.(".digits-form")) applyDigits(root);
    };
    const onClick = (event) => {
      const link = event.target.closest?.("a");
      if (link && root.contains(link)) {
        event.preventDefault();
        const next = link.getAttribute("data-region");
        if (next && next !== regionRef.current && panels[next]) {
          provinceRef.current = "";
          setRegion(next);
          return;
        }
        if (link.id === "lucky-number-btn") {
          link.classList.add("loading-opacity");
          track("lucky-link", setTimeout(() => link.classList.remove("loading-opacity"), 3000));
        }
      }
      if (event.target.closest?.(".trial-button")) {
        event.preventDefault();
        startTrial(root, regionRef.current);
        return;
      }
      const gdb = event.target.closest?.(".gdb-button button");
      if (gdb) {
        const prize = root.querySelector(".prize");
        const message = root.querySelector(".trial-box-text");
        if (!prize || !message) return;
        gdb.textContent = "Đang quay thử";
        let left = 15;
        message.textContent = `Bộ số sẽ xuất hiện sau ${left} giây bạn nhé!`;
        track(
          "gdb-spin",
          setInterval(() => {
            prize.textContent = String(Math.floor(10000 + Math.random() * 90000));
          }, 100)
        );
        track(
          "gdb-count",
          setInterval(() => {
            left -= 1;
            if (left >= 0) message.textContent = `Bộ số sẽ xuất hiện sau ${left} giây bạn nhé!`;
          }, 1000)
        );
        track(
          "gdb-done",
          setTimeout(() => {
            clearTimer("gdb-spin");
            clearTimer("gdb-count");
            const value = String(Math.floor(10000 + Math.random() * 90000));
            prize.textContent = value;
            message.innerHTML = `Giải đặc biệt <span class="clred"> ${value} </span> xuất hiện trong kỳ mở thưởng ngày ${todayLabel()}`;
            gdb.textContent = "Bắt đầu quay";
          }, 15000)
        );
        return;
      }
      if (event.target.closest?.("#lucky-loto")) {
        const box = root.querySelector("#quay-thu-loto");
        const target = root.querySelector("#loto-may-man");
        if (!box || !target) return;
        const count = Number(target.getAttribute("data-number") || 5);
        box.classList.add("loading-opacity");
        track(
          "loto",
          setTimeout(() => {
            const used = new Set();
            const rows = [];
            while (rows.length < count) {
              const number = pad(Math.floor(Math.random() * 100));
              if (used.has(number)) continue;
              used.add(number);
              const seed = hashText(number);
              rows.push(
                `<tr><td class="clred"><strong>${number}</strong></td><td>${seed % 12}</td><td>${pad((seed % 27) + 1)}-10-2026</td><td>${(seed % 20) + 1}</td></tr>`
              );
            }
            target.innerHTML = `<div class="pad5"><table class="mag0"><tr><th>Bộ số</th><th>Số lần về 30 ngày</th><th>Ngày về gần nhất</th><th>Số ngày chưa về</th></tr>${rows.join("")}</table></div>`;
            box.classList.remove("loading-opacity");
          }, 3000)
        );
        return;
      }
      if (event.target.closest?.("#quaythu_mega_btn")) {
        spinBalls(root, "mega");
        return;
      }
      if (event.target.closest?.("#quaythu_power_btn")) {
        spinBalls(root, "power");
        return;
      }
      if (event.target.closest?.(".submitmm")) {
        const day = Number(root.querySelector("#mySelect_ngay")?.value || 0);
        const month = Number(root.querySelector("#mySelect_thang")?.value || 0);
        const year = Number(root.querySelector("#mySelect")?.value || 0);
        if (!day || !month || !year) return;
        const seed = hashText(`${year}-${month}-${day}`);
        const [element, color] = MENH[year % 5];
        const setText = (id, value) => {
          const node = root.querySelector(`#${id}`);
          if (node) node.textContent = value;
        };
        setText("show_ngay", `${pad(day)}/${pad(month)}/${year}`);
        setText("cung_hd", zodiac(day, month));
        setText("sao", `${CAN[(year + 6) % 10]} ${CHI[(year + 8) % 12]}`);
        setText("mau_sac", `${element} - ${color}`);
        const pair = (shift) => pad((seed >> shift) % 100);
        setText("showkq1", pair(0));
        setText("showkq2", pair(3));
        setText("showkq22", pair(6));
        const special = root.querySelector(".showkq3");
        const lot = root.querySelector(".showkq32");
        if (special) special.textContent = pad(seed % 100000, 5);
        if (lot) lot.textContent = pair(9);
        const result = root.querySelector("#ketqua");
        const again = root.querySelector(".xemlai");
        if (result) result.style.display = "block";
        if (again) again.style.display = "inline-block";
        return;
      }
      if (event.target.closest?.(".xemlai")) {
        const result = root.querySelector("#ketqua");
        const again = root.querySelector(".xemlai");
        if (result) result.style.display = "none";
        if (again) again.style.display = "none";
        return;
      }
      const question = event.target.closest?.(".faq-question");
      if (question) {
        const answer = question.nextElementSibling;
        if (!answer) return;
        const open = answer.style.display === "block";
        answer.style.display = open ? "none" : "block";
        question.querySelector(".toggle-icon")?.classList.toggle("active", !open);
        return;
      }
      const prev = event.target.closest?.(".xsmn-swiper-button-prev");
      const next = event.target.closest?.(".xsmn-swiper-button-next");
      if (prev || next) {
        const wrap = root.querySelector(".xsmn-swiper-wrapper");
        if (!wrap) return;
        const current = Number(wrap.dataset.offset || 0);
        const max = Math.max(0, wrap.scrollWidth - (wrap.parentElement?.clientWidth || 0));
        const offset = Math.max(0, Math.min(max, current + (next ? 140 : -140)));
        wrap.dataset.offset = String(offset);
        wrap.style.transform = `translateX(${-offset}px)`;
      }
    };
    root.addEventListener("submit", onSubmit);
    root.addEventListener("change", onChange);
    root.addEventListener("click", onClick);
    return () => {
      clearAll();
      root.removeEventListener("submit", onSubmit);
      root.removeEventListener("change", onChange);
      root.removeEventListener("click", onClick);
    };
  }, []);

  return <div ref={rootRef} />;
}

function QuayThuV4LightHost({ config }) {
  const hostRef = useRef(null);
  const reactRootRef = useRef(null);
  const region = resolveRegion(config?.region);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: "open" });
    let style = shadow.querySelector("style[data-qtv4]");
    if (!style) {
      style = document.createElement("style");
      style.setAttribute("data-qtv4", "");
      shadow.appendChild(style);
    }
    style.textContent = cssText;
    let mount = shadow.querySelector("[data-qtv4-mount]");
    if (!mount) {
      mount = document.createElement("div");
      mount.setAttribute("data-qtv4-mount", "");
      shadow.appendChild(mount);
    }
    if (!reactRootRef.current) reactRootRef.current = createRoot(mount);
    reactRootRef.current.render(<QuayThuV4Light region={region} />);
    return () => {
      reactRootRef.current?.unmount();
      reactRootRef.current = null;
    };
  }, [region]);

  return <div ref={hostRef} className="qtv4-host" style={HOST_STYLE} />;
}

export default QuayThuV4LightHost;
